/**
 * v38.7 — Tenant Subdomain Detector
 *
 * Inspects the current hostname and decides whether the SPA is loaded under
 * a tenant subdomain (e.g. prashanti.klickniramaya.com) or as the platform
 * root (e.g. app.klickniramaya.com or klickniramaya.com).
 *
 * Hostnames that are NOT considered tenant subdomains:
 *   - localhost (and 127.0.0.1)
 *   - the bare apex domain (klickniramaya.com)
 *   - www.* and app.* and admin.* (reserved platform subdomains)
 *   - Emergent preview hosts (*.emergent.host, *.preview.emergentagent.com)
 *     because tests run on these and they should NOT be auto-scoped to a tenant.
 */

const PLATFORM_SUBDOMAINS = new Set(['www', 'app', 'admin', 'api', 'staging', 'dev', 'qa']);

const isPreviewHost = (hostname) =>
  hostname.endsWith('.emergent.host') ||
  hostname.endsWith('.preview.emergentagent.com') ||
  hostname.endsWith('.emergentagent.com');

export const detectTenantSlug = (hostname = window.location.hostname) => {
  if (!hostname) return null;
  const host = hostname.toLowerCase();
  // 1) Localhost / IP — no tenant.
  if (host === 'localhost' || /^[\d.]+$/.test(host)) return null;
  // 2) Emergent preview hosts — treat as platform view (super-admin can still pick
  //    a hospital from the sidebar).
  if (isPreviewHost(host)) return null;
  // 3) Decompose host into parts.
  const parts = host.split('.');
  // klickniramaya.com → 2 parts, no subdomain → platform.
  if (parts.length < 3) return null;
  // The very first label is the candidate slug; everything after it must form a
  // valid root domain (≥2 labels).
  const first = parts[0];
  // Reserved platform subdomains → not a tenant.
  if (PLATFORM_SUBDOMAINS.has(first)) return null;
  // Multi-level subdomains (e.g. dev.prashanti.klickniramaya.com) — still treat
  // the first label as the tenant slug. Adjust here if you need different logic.
  return first;
};

/**
 * v38.8 — Helper used by the root-domain hospital dropdown to compute the
 * apex/root host (e.g. `klickniramaya.com`) so we can build absolute redirect
 * URLs like `https://<slug>.<rootDomain>` regardless of whether the user
 * landed on `klickniramaya.com`, `www.klickniramaya.com`, or `app.klickniramaya.com`.
 *
 * Returns `null` for hosts where a subdomain redirect makes no sense
 * (localhost, IPs, Emergent preview hosts). Callers should fall back to
 * `?hospital=<code>` query-param navigation in those cases.
 */
export const getRootDomain = (hostname = window.location.hostname) => {
  if (!hostname) return null;
  const host = hostname.toLowerCase();
  if (host === 'localhost' || /^[\d.]+$/.test(host)) return null;
  if (isPreviewHost(host)) return null;
  const parts = host.split('.');
  // 2-label apex (e.g. klickniramaya.com) — use as-is.
  if (parts.length === 2) return host;
  // 3+ labels — strip the first label if it is a known platform subdomain
  // (www/app/admin/api/staging/dev/qa). Otherwise the user is already inside a
  // tenant subdomain (which means our dropdown shouldn't have been shown
  // anyway) — fall back to dropping the first label.
  return parts.slice(1).join('.');
};

/**
 * v38.8 — Conditions under which the root login page should attempt a
 * subdomain redirect on hospital selection. False ⇒ stay on the current
 * page and use the legacy `?hospital=<code>` query-param so QA / Emergent
 * preview can still test all hospital logins.
 */
export const canRedirectToSubdomain = (hostname = window.location.hostname) => {
  if (!hostname) return false;
  const host = hostname.toLowerCase();
  if (host === 'localhost' || /^[\d.]+$/.test(host)) return false;
  if (isPreviewHost(host)) return false;
  return host.split('.').length >= 2;
};

/**
 * Apply tenant branding hints to the document (favicon + page title).
 * Called by App.js after the slug has been resolved to a hospital.
 */
export const applyTenantBranding = ({ name, logo_url, primary_color }) => {
  if (name) {
    document.title = `${name} · KLICK NIRAMAYA HMS`;
  }
  if (logo_url) {
    let link = document.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = logo_url;
  }
  if (primary_color) {
    document.documentElement.style.setProperty('--tenant-primary', primary_color);
  }
};
